import { Injectable } from '@angular/core';
import { createStore, select, setProp, setProps, withProps } from '@ngneat/elf';

import { persistState, localStorageStrategy } from '@ngneat/elf-persist-state';
import { isToday, subWeeks } from 'date-fns';
import { isCanonicalWeekKey, weekKeyFor } from 'src/app/utilities/weeks-review.utils';
import { ELF_PREFIX } from 'src/app/app.constants';
import { DEFAULT_DAY_START_HOUR } from 'src/app/utilities/day-key.utils';
import { UserSettings } from './settings.interface';
import { Subject, tap } from 'rxjs';

interface LocalSettingsProps extends UserSettings {
  notificationEnabled: boolean;

  focusTimerReminders: string[];

  accumulateRest: boolean;
  showRestBalance: boolean;
  preserveTimer: boolean;
  lastAliveTimestamp: number;

  // Last-writer-wins marker for settings sync: the `updatedAt` of the last remote settings state we
  // applied OR the last local write we sent. Persisted (survives restart), never part of the synced
  // UserSettings payload. See FirebaseSyncService settings sync.
  settingsUpdatedAt: number;

  settingsCardExpanded: boolean;

  isMenuVisible: boolean;
  // Weekly review window (4 | 8 | 12 completed weeks). View preference, local-only — never synced.
  weeklyReviewWindowWeeks: number;
  isSidebarItemVisible: { [key: string]: boolean };
  onboardingCompleted: boolean;
  websiteBlockerEnabled: boolean;
  webBlockerRequirementsVisible: boolean;

  whitelistItems: string;
  hyperBlockEnabled: boolean;

  // One-time local repair version guard (see HistoryRepairService.repairOnce). Local bookkeeping,
  // never a synced user setting — accessors use setProp so they never emit
  // manualUserSettingsChange$, and both fields are deliberately absent from getUserSettings()'s
  // explicit payload.
  historyRepairVersion: number;

  // The uid last confirmed to own this device's local history (stamped on every successful
  // FirebaseSyncService.start(uid), read before it advances). Lets a sign-in quarantine any days
  // that survived a sign-out (clearSyncedDays deliberately keeps anything not provably synced) but
  // belong to a DIFFERENT account, so they can never reach a new account's backfill. null until the
  // very first sign-in on this device, which deliberately means "no known prior owner" — local-only
  // ("guest") data created before any sign-in is never quarantined. Local bookkeeping, never synced.
  lastSyncedUid: string | null;
}

const initialState: LocalSettingsProps = {
  notificationEnabled: false,
  focusReminderEnabled: true,
  endTimerReminderEnabled: true,
  focusReminderInterval: 5,
  accumulateRest: true,
  showRestBalance: true,
  preserveTimer: true,
  settingsCardExpanded: true,
  restSoundEnabled: true,
  focusTimerReminders: [
    'Stay sharp, stay bright. Keep the focus tight!',
    "Flowing and growing, every moment's a chance!",
    'Eyes on the prize, make this time your dance.',
    "Breathe in focus, exhale success. You've got this!",
    "Ride the wave of productivity. Surf's up!",
    'Unlock your potential, one tick at a time.',
    'Crafting greatness, stitch by stitch.',
    "Keep the momentum, you're on a roll!",
    'Every minute counts. Make it matter.',
    'Focus is your superpower. Wear it well.',
  ],
  lastAliveTimestamp: Date.now(),
  settingsUpdatedAt: 0,
  shouldCelebrate: true,
  dayStartHour: DEFAULT_DAY_START_HOUR,
  isMenuVisible: false,
  weeklyTargetMinutes: null,
  weekTargetOverrides: {},
  weeklyReviewStartWeek: null,
  weeklyReviewWindowWeeks: 8,
  isSidebarItemVisible: {},
  onboardingCompleted: false,
  websiteBlockerEnabled: false,
  webBlockerRequirementsVisible: true,
  whitelistItems: '',
  hyperBlockEnabled: false,
  historyRepairVersion: 0,
  lastSyncedUid: null,
};

const WEEKLY_REVIEW_WINDOW_OPTIONS = [4, 8, 12];
const MAX_WEEKLY_TARGET_MINUTES = 10080; // minutes in a week
const OVERRIDE_RETENTION_WEEKS = 52;

const name = ELF_PREFIX + 'settings';

const store = createStore({ name }, withProps<LocalSettingsProps>(initialState));

export const persist = persistState(store, {
  key: name,
  storage: localStorageStrategy,
});

@Injectable({
  providedIn: 'root',
})
export class SettingsRepository {
  notificationEnabled$ = store.pipe(select((state) => state.notificationEnabled));

  focusReminderEnabled$ = store.pipe(select((state) => state.focusReminderEnabled));
  focusReminderInterval$ = store.pipe(select((state) => state.focusReminderInterval));

  endTimerReminderEnabled$ = store.pipe(select((state) => state.endTimerReminderEnabled));
  restSoundEnabled$ = store.pipe(select((state) => state.restSoundEnabled));

  accumulateRest$ = store.pipe(select((state) => state.accumulateRest));
  showRestBalance$ = store.pipe(select((state) => state.showRestBalance !== false));

  preserveTimer$ = store.pipe(select((state) => state.preserveTimer));

  settingsExpanded$ = store.pipe(select((state) => state.settingsCardExpanded));

  celebrationEnabled$ = store.pipe(select((state) => state.shouldCelebrate));

  isMenuVisible$ = store.pipe(select((state) => state.isMenuVisible));

  weeklyTargetMinutes$ = store.pipe(select((state) => state.weeklyTargetMinutes ?? null));
  weekTargetOverrides$ = store.pipe(select((state) => state.weekTargetOverrides ?? {}));
  weeklyReviewWindowWeeks$ = store.pipe(
    select((state) => this.normalizeWindowWeeks(state.weeklyReviewWindowWeeks)),
  );
  weeklyReviewStartWeek$ = store.pipe(
    select((state) => this.sanitizeStartWeek(state.weeklyReviewStartWeek)),
  );

  manualUserSettingsChange$ = new Subject<UserSettings>();

  isSidebarItemVisible$ = store.pipe(select((state) => state.isSidebarItemVisible));

  isOnboardingCompleted$ = store.pipe(select((state) => state.onboardingCompleted));

  websiteBlockerEnabled$ = store.pipe(select((state) => state.websiteBlockerEnabled));
  isWebBlockerRequirementsVisible$ = store.pipe(
    select((state) => state.webBlockerRequirementsVisible),
  );
  whitelistItems$ = store.pipe(select((state) => state.whitelistItems));
  hyperBlockEnabled$ = store.pipe(select((state) => state.hyperBlockEnabled));

  constructor() {}

  // General Notifications
  setNotificationEnabled(notificationEnabled: boolean) {
    store.update(setProp('notificationEnabled', notificationEnabled));
  }

  isNotificationEnabled() {
    return store.query((state) => state.notificationEnabled);
  }

  toggleNotification() {
    const notificationEnabled = store.query((state) => state.notificationEnabled);
    store.update(setProp('notificationEnabled', !notificationEnabled));
    this.emitUserSettings();
  }

  // Focus reminders
  toggleFocusReminder() {
    const focusReminderEnabled = store.query((state) => state.focusReminderEnabled);
    store.update(setProp('focusReminderEnabled', !focusReminderEnabled));
    this.emitUserSettings();
  }

  isFocusReminderEnabled() {
    return store.query((state) => state.focusReminderEnabled);
  }

  setFocusReminderInterval(interval: number) {
    store.update(setProp('focusReminderInterval', interval));
    this.emitUserSettings();
  }

  getFocusReminderInterval() {
    return store.query((state) => state.focusReminderInterval);
  }

  getRemindersList() {
    return store.query((state) => state.focusTimerReminders);
  }

  // End Timer Reminder
  toggleEndTimerReminder() {
    const endTimerReminderEnabled = store.query((state) => state.endTimerReminderEnabled);
    store.update(setProp('endTimerReminderEnabled', !endTimerReminderEnabled));
    this.emitUserSettings();
  }

  isEndTimerReminderEnabled() {
    return store.query((state) => state.endTimerReminderEnabled);
  }

  toggleEndRestSound() {
    const soundEnabled = store.query((state) => state.restSoundEnabled);
    store.update(setProp('restSoundEnabled', !soundEnabled));
    this.emitUserSettings();
  }

  isSoundEnabled() {
    return store.query((state) => state.restSoundEnabled);
  }

  //Accumulate Rest
  toggleAccumulateRest() {
    const accumulateRest = store.query((state) => state.accumulateRest);
    store.update(setProp('accumulateRest', !accumulateRest));
  }

  getAccumulateRest() {
    return store.query((state) => state.accumulateRest);
  }

  // Rest balance visibility is a local display preference, like Appearance.
  toggleRestBalance() {
    const showRestBalance = store.query((state) => state.showRestBalance !== false);
    store.update(setProp('showRestBalance', !showRestBalance));
  }

  isRestBalanceVisible() {
    return store.query((state) => state.showRestBalance !== false);
  }

  //Preserve Timer
  togglePreserveTimer() {
    const preserveTimer = store.query((state) => state.preserveTimer);
    store.update(setProp('preserveTimer', !preserveTimer));
  }

  getPreserveTimer() {
    return store.query((state) => state.preserveTimer);
  }

  // Day start hour (logical midnight). Emits a manual change so FirebaseSyncService persists it.
  // Clamped on READ as well as on write (I3): setDayStartHour only ever stores an in-range value
  // for hours WRITTEN through it, but setUserSettings (the remote-settings apply path, see below)
  // spreads a remote document's fields straight into the store with no validation of its own — a
  // corrupted or hand-edited dayStartHour there must not silently shift every day key.
  getDayStartHour(): number {
    return this.clampDayStartHour(store.query((state) => state.dayStartHour));
  }

  setDayStartHour(hour: number) {
    if (!Number.isInteger(hour)) {
      return;
    }
    store.update(setProp('dayStartHour', this.clampDayStartHour(hour)));
    this.emitUserSettings();
  }

  private clampDayStartHour(hour: unknown): number {
    return Number.isInteger(hour)
      ? Math.min(23, Math.max(0, hour as number))
      : DEFAULT_DAY_START_HOUR;
  }

  // Alive Timestamp
  setLastAliveTimestamp(timestamp: number = Date.now()) {
    store.update(setProp('lastAliveTimestamp', timestamp));
  }

  getLastAliveTimestamp() {
    return store.query((state) => state.lastAliveTimestamp);
  }

  // Settings sync marker (last-writer-wins). setProp does NOT emit manualUserSettingsChange$, so
  // advancing the marker never triggers a local->remote settings write.
  getSettingsUpdatedAt() {
    return store.query((state) => state.settingsUpdatedAt);
  }

  setSettingsUpdatedAt(timestamp: number) {
    store.update(setProp('settingsUpdatedAt', timestamp));
  }

  // Expanded
  toggleExpanded() {
    const expanded = store.query((state) => state.settingsCardExpanded);
    store.update(setProp('settingsCardExpanded', !expanded));
  }

  //Celebration
  toggleCelebration() {
    const shouldCelebrate = store.query((state) => state.shouldCelebrate);
    store.update(setProp('shouldCelebrate', !shouldCelebrate));
    this.emitUserSettings();
  }

  isCelebrationEnabled() {
    return store.query((state) => state.shouldCelebrate);
  }

  // Menu
  toggleMenu() {
    const isMenuVisible = store.query((state) => state.isMenuVisible);
    store.update(setProp('isMenuVisible', !isMenuVisible));
  }

  isMenuVisible() {
    return store.query((state) => state.isMenuVisible);
  }

  // Sidebar Item
  toggleSidebarItem(id: string) {
    const sidebarItemVisible = store.query((state) => state.isSidebarItemVisible);
    const isSidebarItemVisible =
      sidebarItemVisible[id] === undefined ? true : sidebarItemVisible[id];

    store.update(
      setProp('isSidebarItemVisible', { ...sidebarItemVisible, [id]: !isSidebarItemVisible }),
    );
  }

  // website blocker
  toggleWebsiteBlocker() {
    const websiteBlockerEnabled = store.query((state) => state.websiteBlockerEnabled);
    store.update(setProp('websiteBlockerEnabled', !websiteBlockerEnabled));
  }

  isWebsiteBlockerEnabled() {
    return store.query((state) => state.websiteBlockerEnabled);
  }

  toggleWebBlockerRequirements() {
    const webBlockerRequirementsVisible = store.query(
      (state) => state.webBlockerRequirementsVisible,
    );
    store.update(setProp('webBlockerRequirementsVisible', !webBlockerRequirementsVisible));
  }

  setWhitelistItems(whitelistItems: string) {
    store.update(setProp('whitelistItems', whitelistItems));
  }

  getWhitelistItems() {
    return store.query((state) => state.whitelistItems);
  }

  toggleHyperBlock() {
    const hyperBlockEnabled = store.query((state) => state.hyperBlockEnabled);
    store.update(setProp('hyperBlockEnabled', !hyperBlockEnabled));
  }

  getHyperBlockEnabled() {
    return store.query((state) => state.hyperBlockEnabled);
  }

  // setOnboardingCompleted
  setOnboardingCompleted(completed: boolean) {
    store.update(setProp('onboardingCompleted', completed));
  }

  // ---- Weekly review (spec: docs/superpowers/specs/2026-08-25-weekly-review-design.md) ----
  // Target + overrides are synced UserSettings (setters emit manualUserSettingsChange$);
  // the window is a local-only view preference (setProp never emits).

  getWeeklyTargetMinutes(): number | null {
    return this.clampWeeklyTargetMinutes(store.query((state) => state.weeklyTargetMinutes));
  }

  setWeeklyTargetMinutes(minutes: number | null) {
    store.update(setProp('weeklyTargetMinutes', this.clampWeeklyTargetMinutes(minutes)));
    this.emitUserSettings();
  }

  getWeekTargetOverrides(): Record<string, number> {
    return { ...store.query((state) => state.weekTargetOverrides) };
  }

  /** Sets (minutes) or clears (null) one week's override, pruning stale entries as it writes. */
  setWeekTargetOverride(weekKey: string, minutes: number | null) {
    if (!isCanonicalWeekKey(weekKey)) {
      return;
    }
    // An anchor older than the retention window extends it: weeks inside the anchored
    // ledger stay overridable no matter how old they get.
    const retention = weekKeyFor(subWeeks(new Date(), OVERRIDE_RETENTION_WEEKS));
    const startWeek = this.getWeeklyReviewStartWeek();
    const cutoff = startWeek !== null && startWeek < retention ? startWeek : retention;
    const overrides: Record<string, number> = Object.fromEntries(
      Object.entries(store.query((state) => state.weekTargetOverrides)).filter(
        ([key]) => key >= cutoff,
      ),
    );

    if (minutes === null) {
      delete overrides[weekKey];
    } else {
      const clamped = this.clampWeeklyTargetMinutes(minutes);
      if (clamped === null) {
        return;
      }
      overrides[weekKey] = clamped;
    }

    store.update(setProp('weekTargetOverrides', overrides));
    this.emitUserSettings();
  }

  getWeeklyReviewStartWeek(): string | null {
    return this.sanitizeStartWeek(store.query((state) => state.weeklyReviewStartWeek));
  }

  setWeeklyReviewStartWeek(weekKey: string | null) {
    if (weekKey !== null && !isCanonicalWeekKey(weekKey)) {
      return;
    }
    store.update(setProp('weeklyReviewStartWeek', weekKey));
    this.emitUserSettings();
  }

  private sanitizeStartWeek(value: unknown): string | null {
    return typeof value === 'string' && isCanonicalWeekKey(value) ? value : null;
  }

  getWeeklyReviewWindowWeeks(): number {
    return this.normalizeWindowWeeks(store.query((state) => state.weeklyReviewWindowWeeks));
  }

  setWeeklyReviewWindowWeeks(weeks: number) {
    if (!WEEKLY_REVIEW_WINDOW_OPTIONS.includes(weeks)) {
      return;
    }
    store.update(setProp('weeklyReviewWindowWeeks', weeks));
  }

  private normalizeWindowWeeks(weeks: unknown): number {
    return typeof weeks === 'number' && WEEKLY_REVIEW_WINDOW_OPTIONS.includes(weeks) ? weeks : 8;
  }

  private clampWeeklyTargetMinutes(minutes: unknown): number | null {
    return Number.isInteger(minutes)
      ? Math.min(MAX_WEEKLY_TARGET_MINUTES, Math.max(0, minutes as number))
      : null;
  }

  private sanitizeWeekTargetOverrides(value: unknown): Record<string, number> | null {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return null; // caller treats the field as absent
    }
    const sanitized: Record<string, number> = {};
    for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
      if (!isCanonicalWeekKey(key)) {
        continue;
      }
      const clamped = this.clampWeeklyTargetMinutes(raw);
      if (clamped !== null) {
        sanitized[key] = clamped;
      }
    }
    return sanitized;
  }

  getUserSettings(): UserSettings {
    const state = store.query((state) => state);
    return {
      focusReminderEnabled: state.focusReminderEnabled,
      endTimerReminderEnabled: state.endTimerReminderEnabled,
      restSoundEnabled: state.restSoundEnabled,
      focusReminderInterval: state.focusReminderInterval,
      shouldCelebrate: state.shouldCelebrate,
      dayStartHour: this.getDayStartHour(),
      weeklyTargetMinutes: this.clampWeeklyTargetMinutes(state.weeklyTargetMinutes),
      weekTargetOverrides: { ...state.weekTargetOverrides },
      weeklyReviewStartWeek: this.sanitizeStartWeek(state.weeklyReviewStartWeek),
    };
  }

  // Applies a remote settings document (I3: validated, not trusted as-is). Whitelists the real
  // UserSettings fields one by one rather than spreading the whole document into the store: a
  // stray key on the remote doc (e.g. a hand-edited or legacy `lastSyncedUid`/`historyRepairVersion`
  // — both are LOCAL-ONLY bookkeeping, never part of the synced payload, but nothing stops a
  // corrupted or manually-edited remote document from carrying one) would otherwise overwrite that
  // local marker straight from an untrusted remote value. A corrupted or hand-edited remote
  // dayStartHour (e.g. 47) is additionally clamped exactly like setDayStartHour does for a local
  // write, before it ever reaches the store.
  setUserSettings(settings: Partial<UserSettings>) {
    const validated: Partial<UserSettings> = {};
    if ('focusReminderEnabled' in settings) {
      validated.focusReminderEnabled = settings.focusReminderEnabled;
    }
    if ('endTimerReminderEnabled' in settings) {
      validated.endTimerReminderEnabled = settings.endTimerReminderEnabled;
    }
    if ('restSoundEnabled' in settings) {
      validated.restSoundEnabled = settings.restSoundEnabled;
    }
    if ('focusReminderInterval' in settings) {
      validated.focusReminderInterval = settings.focusReminderInterval;
    }
    if ('shouldCelebrate' in settings) {
      validated.shouldCelebrate = settings.shouldCelebrate;
    }
    if ('dayStartHour' in settings) {
      validated.dayStartHour = this.clampDayStartHour(settings.dayStartHour);
    }
    if ('weeklyTargetMinutes' in settings) {
      validated.weeklyTargetMinutes = this.clampWeeklyTargetMinutes(settings.weeklyTargetMinutes);
    }
    if ('weekTargetOverrides' in settings) {
      const sanitized = this.sanitizeWeekTargetOverrides(settings.weekTargetOverrides);
      if (sanitized !== null) {
        validated.weekTargetOverrides = sanitized;
      }
    }
    if ('weeklyReviewStartWeek' in settings) {
      validated.weeklyReviewStartWeek = this.sanitizeStartWeek(settings.weeklyReviewStartWeek);
    }
    store.update(setProps((state) => ({ ...state, ...validated })));
  }

  private emitUserSettings() {
    this.manualUserSettingsChange$.next(this.getUserSettings());
  }

  // History repair version guard (see HistoryRepairService.repairOnce). setProp so these never
  // emit manualUserSettingsChange$ — local bookkeeping, not synced user settings.
  getHistoryRepairVersion(): number {
    return store.query((state) => state.historyRepairVersion) ?? 0;
  }

  setHistoryRepairVersion(version: number) {
    store.update(setProp('historyRepairVersion', version));
  }

  // Sign-in ownership marker (see FirebaseSyncService.start / the C2 fix). setProp so this never
  // emits manualUserSettingsChange$.
  getLastSyncedUid(): string | null {
    return store.query((state) => state.lastSyncedUid) ?? null;
  }

  setLastSyncedUid(uid: string | null) {
    store.update(setProp('lastSyncedUid', uid));
  }
}
